import type { ProfileAward, ProfileSpotlight, UpcomingPerformance } from "./profileContent";
import { safeWebUrl } from "./profileContent";

export class ProfileApiError extends Error {
  constructor(public status: number) { super(`Profile request failed (${status})`); }
}

export interface PublicPage<T, C> {
  items: T[];
  hasMore: boolean;
  nextCursor?: C;
}

interface PublicResponse<T> { is_success: boolean; value: T; }
interface SpotlightRecord { idx: number; userIdx: number; content?: string; }
export interface AwardBlock {
  awardIdx: number;
  awardGroup: number | null;
  kind: "WORK" | "PERSON";
  result: string;
  performance?: { performanceIdx?: number; titleEn?: string; titleLocal?: string };
  performanceText?: string;
}
export interface AwardSection {
  ceremony: string;
  awardYear: number;
  category: string;
  categoryEn?: string;
  works?: AwardBlock[];
  persons?: AwardBlock[];
}
interface AwardResponse {
  success: boolean;
  data: { userIdx: number; sections: AwardSection[]; truncated: boolean };
}

async function publicGet<T>(path: string, params: Record<string, string>, isMsg = true, signal?: AbortSignal): Promise<T> {
  const endpoint = isMsg ? process.env.REACT_APP_MSG_SERVER_ENDPOINT : process.env.REACT_APP_API_SERVER_ENDPOINT;
  const url = new URL(path, endpoint);
  Object.entries(params).forEach(([key, value]) => url.searchParams.set(key, value));
  const controller = new AbortController();
  const abort = () => controller.abort();
  signal?.addEventListener("abort", abort, { once: true });
  if (signal?.aborted) abort();
  const timeout = setTimeout(abort, 10000);
  try {
    // 공개 조회에 쿠키·인증 헤더 및 불필요한 preflight 헤더 제외
    const response = await fetch(url.toString(), { credentials: "omit", signal: controller.signal, cache: "no-store" });
    if (!response.ok) throw new ProfileApiError(response.status);
    return await response.json();
  } finally {
    clearTimeout(timeout);
    signal?.removeEventListener("abort", abort);
  }
}

export async function getUpcomingPerformances(handle: string, cursor?: string, signal?: AbortSignal): Promise<PublicPage<UpcomingPerformance, string>> {
  const params: Record<string, string> = { handle, size: "20" };
  if (cursor !== undefined) params.cursor = cursor;
  const response = await publicGet<PublicResponse<{ performances: UpcomingPerformance[]; hasMore: boolean; nextCursor?: string }>>("/oapi/perf/credit/soon", params, true, signal);
  if (!response.is_success || !Array.isArray(response.value?.performances)) throw new ProfileApiError(502);
  const { performances, hasMore, nextCursor } = response.value;
  return { items: performances, hasMore, nextCursor };
}

export async function getUserSpotlights(handle: string, cursor?: number, signal?: AbortSignal): Promise<PublicPage<ProfileSpotlight, number>> {
  const params: Record<string, string> = { handle };
  if (cursor !== undefined) params.lastIdx = String(cursor);
  const response = await publicGet<PublicResponse<{ slList: SpotlightRecord[]; hasMore: boolean; nextLastIdx?: number }>>("/oapi/spotlight/user", params, true, signal);
  if (!response.is_success || !Array.isArray(response.value?.slList)) throw new ProfileApiError(502);
  const items = response.value.slList.map(item => {
    const image = new URL("/api/sl/g/img", process.env.REACT_APP_API_SERVER_ENDPOINT);
    image.search = new URLSearchParams({ userIdx: String(item.userIdx), slIdx: String(item.idx), size: "s" }).toString();
    return { id: String(item.idx), thumbnailUrl: image.toString(), caption: item.content };
  });
  return { items, hasMore: response.value.hasMore, nextCursor: response.value.nextLastIdx };
}

export function mapProfileAwards(sections: AwardSection[]): ProfileAward[] {
  // 공동수상자는 개별 행으로 늘리지 않고 서버의 WORK/PERSON 블록 단위 유지
  return sections.flatMap((section, sectionIndex) => [...(section.works || []), ...(section.persons || [])].map(block => ({
    id: `${sectionIndex}:${block.kind}:${block.awardGroup ?? "ungrouped"}:${block.awardIdx}`,
    category: section.categoryEn || section.category,
    ceremony: section.ceremony,
    year: section.awardYear,
    result: block.result === "Nominated" ? "Nominee" : block.result,
    performanceTitle: block.performance?.titleEn || block.performance?.titleLocal || block.performanceText,
    performanceIdx: block.performance?.performanceIdx,
  })));
}

export async function getUserAwards(handle: string, signal?: AbortSignal) {
  const response = await publicGet<AwardResponse>("/api/award-title/user", { handle }, true, signal);
  if (!response.success || !Array.isArray(response.data?.sections)) throw new ProfileApiError(502);
  return { userIdx: response.data.userIdx, items: mapProfileAwards(response.data.sections), truncated: response.data.truncated };
}

export async function getPortfolioShareUrl(userIdx: number, signal?: AbortSignal) {
  if (!Number.isSafeInteger(userIdx) || userIdx <= 0) throw new ProfileApiError(400);
  const response = await publicGet<{ data?: { url?: string } }>("/oapi/directory/web-profiles/share-link", { userIdx: String(userIdx), lang: "en" }, false, signal);
  const url = response.data?.url;
  if (!url || !/^https?:\/\//i.test(url) || !safeWebUrl(url)) throw new ProfileApiError(502);
  // 환경별 도메인과 공유 코드의 서버 응답 원형 유지
  return url;
}
